import { makeStyles } from '@material-ui/core/styles'
import { colors } from 'src/components/app-ui'

const MONO = 'ui-monospace, Menlo, monospace'

const bigButton = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '10px',
  padding: '14px',
  border: 0,
  borderRadius: '10px',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: '15px',
  fontWeight: 600,
  color: '#FFFFFF',
  '&:disabled': {
    cursor: 'default',
    opacity: 0.6,
  },
}

const useStyles = makeStyles({
  view: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    minWidth: 0,
  },
  head: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: '12px',
  },
  title: {
    fontSize: '18px',
    fontWeight: 600,
    color: colors.navy,
  },
  subtitle: {
    marginTop: 2,
    fontSize: '13px',
    color: colors.muted,
  },
  domainButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 10px',
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    background: '#FFFFFF',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '12px',
    color: colors.navy,
  },
  domainList: {
    display: 'flex',
    flexDirection: 'column',
    padding: '4px',
  },
  domainOption: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 12px',
    border: 0,
    borderRadius: '6px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '13px',
    textAlign: 'left',
    '&:hover': {
      background: colors.hover,
    },
  },
  domainKind: {
    fontSize: '11px',
    fontWeight: 500,
    color: colors.subtle,
  },
  domainSetup: {
    marginTop: 4,
    padding: '8px 12px',
    border: 0,
    borderTop: '1px solid #F1F3F5',
    background: 'transparent',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '12px',
    textAlign: 'left',
    color: colors.primary,
  },
  linkCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '14px 16px',
    border: `1px solid ${colors.border}`,
    borderRadius: '10px',
  },
  linkTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  linkTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: colors.navy,
  },
  badge: {
    padding: '2px 6px',
    borderRadius: '4px',
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '.06em',
  },
  linkRow: {
    display: 'flex',
    gap: '8px',
  },
  linkInput: {
    flex: 1,
    minWidth: 0,
    padding: '9px 12px',
    border: `1px solid ${colors.borderSoft}`,
    borderRadius: '8px',
    outline: 'none',
    background: '#F9FAFB',
    fontFamily: 'inherit',
    fontSize: '13px',
    color: colors.navy,
  },
  copyButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '0 14px',
    border: 0,
    borderRadius: '8px',
    background: colors.primary,
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '13px',
    fontWeight: 500,
    color: '#FFFFFF',
    whiteSpace: 'nowrap',
  },
  copied: {
    background: colors.successDark,
  },
  openButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '0 14px',
    border: '1px solid rgba(1,95,251,.5)',
    borderRadius: '8px',
    background: '#FFFFFF',
    fontSize: '13px',
    fontWeight: 500,
    color: colors.primary,
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    '&:hover': {
      background: 'rgba(1,95,251,.06)',
    },
  },
  hint: {
    fontSize: '12px',
    lineHeight: 1.5,
    color: colors.muted,
  },
  settingRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '16px',
  },
  settingTitle: {
    fontSize: '14px',
    fontWeight: 500,
    color: colors.navy,
  },
  draftRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '12px',
  },
  draftButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    background: 'transparent',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '13px',
    fontWeight: 500,
    color: colors.navy,
    '&:hover': {
      background: colors.hover,
    },
    '&:disabled': {
      cursor: 'default',
      opacity: 0.6,
    },
  },
  muted: {
    fontSize: '12px',
    color: colors.subtle,
  },
  warning: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    padding: '10px 14px',
    borderRadius: '8px',
    background: '#FFF4E5',
    fontSize: '13px',
    lineHeight: 1.5,
    color: '#8A5A00',
  },
  warningIcon: {
    display: 'inline-flex',
    marginTop: 2,
  },
  note: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 14px',
    borderRadius: '10px',
    fontSize: '13px',
    color: colors.navy,
  },
  noteIcon: {
    display: 'inline-flex',
    color: colors.muted,
  },
  noteText: {
    fontWeight: 500,
  },
  noteUrl: {
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    padding: '2px 8px',
    border: `1px solid ${colors.border}`,
    borderRadius: '6px',
    background: '#FFFFFF',
    fontFamily: MONO,
    fontSize: '12px',
    color: colors.slate,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: colors.navy,
  },
  sectionDesc: {
    marginTop: 4,
    fontSize: '14px',
    lineHeight: 1.5,
    color: colors.muted,
  },
  sizeFields: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
  },
  field: {
    flex: 1,
    minWidth: 140,
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    fontSize: '13px',
    fontWeight: 500,
    color: colors.muted,
  },
  input: {
    padding: '10px 12px',
    border: `1px solid ${colors.borderSoft}`,
    borderRadius: '8px',
    outline: 'none',
    fontFamily: 'inherit',
    fontSize: '14px',
    color: colors.navy,
    '&:focus': {
      borderColor: colors.primary,
    },
  },
  code: {
    margin: 0,
    padding: '16px 20px',
    borderRadius: '10px',
    background: colors.hover,
    fontFamily: MONO,
    fontSize: '13px',
    lineHeight: 1.7,
    color: colors.navy,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
  },
  darkButton: {
    ...bigButton,
    background: colors.navy,
    '&:hover': {
      background: '#001B2B',
    },
  },
  primaryButton: {
    ...bigButton,
    background: colors.primary,
    '&:hover': {
      background: colors.primaryDark,
    },
  },
  copiedButton: {
    background: `${colors.successDark} !important`,
  },
  segmented: {
    alignSelf: 'flex-start',
    display: 'inline-flex',
    padding: '3px',
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    background: '#FFFFFF',
  },
  segment: {
    padding: '7px 16px',
    border: 0,
    borderRadius: '6px',
    background: 'transparent',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '13px',
    fontWeight: 500,
    color: colors.navy,
  },
  segmentOn: {
    background: colors.hover,
    boxShadow: 'inset 0 0 0 1px rgba(0,46,71,.12)',
  },
  access: {
    padding: '0 4px',
    fontSize: '13px',
    color: colors.muted,
    '& strong': {
      fontWeight: 600,
      color: colors.navy,
    },
    '& a': {
      cursor: 'pointer',
    },
  },
})

export default useStyles
